import { Directive, ViewChild, ElementRef, inject, OnDestroy, AfterViewInit } from '@angular/core';
import { Stripe } from '@df/module-stripe/stripe.namespace';
import { AbstractStripeComponent } from '@df/module-stripe/abstract-stripe.component';
import { Status } from '@df/core/status';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { TimeoutService } from '@df/ng/timeout.service';
import { BasketTotalsUpdatingService } from '@df/basket/view/basket-totals-updating.service';

@Directive()
// tslint:disable-next-line: directive-class-suffix
export abstract class StripeExpressCheckoutComponentDef
    extends AbstractStripeComponent<Stripe.IExpressCheckoutPaymentMethod, Stripe.IExpressCheckoutPaymentMethodConfig>
    implements OnDestroy, AfterViewInit
{
    protected override noStripeCache = true;
    /**
     * @type {Status}
     */
    loadExpressCheckoutParamsStatus = new Status();

    /**
     * @type {Status}
     */
    totalsUpdatingClickStatus = new Status();

    /**
     * GP on/off
     * @type {boolean}
     */
    protected googlePayAvailability: Stripe.TExpressCheckoutGooglePayApplePayAvailability = 'always';

    /**
     * AP on/off
     * @type {boolean}
     */
    protected applePayAvailability: Stripe.TExpressCheckoutGooglePayApplePayAvailability = 'always';

    /**
     * Link on/off
     * @type {boolean}
     */
    protected linkAvailability: Stripe.TExpressCheckoutMethodAvailability = 'never';

    /**
     * Express checkout element instance
     * @type {Stripe.IExpressCheckoutElementInstance}
     */
    private expressCheckoutElementInstance?: Stripe.IExpressCheckoutElementInstance;

    /**
     * Data that affects express checkout, on checkout data change we check if this data change -> if it is changed we "recreate"
     * element
     * @type {string}
     */
    private lastUpdateRequiredData?: string;

    /**
     * Method code
     * @type {Stripe.PaymentMethod}
     */
    protected readonly methodCode = Stripe.PaymentMethod.StripePaymentsExpress;

    @ViewChild('container')
    private container?: ElementRef<any>;

    private basketTotalsUpdatingService = inject(BasketTotalsUpdatingService);
    private timeoutService = inject(TimeoutService);
    private onCheckoutDataUpdateDebounced = this.timeoutService.debounce(() => this.onCheckoutDataUpdate(), 1000);

    // private shippingAddress?: Stripe.IAddress;

    override ngAfterViewInit(): void {
        super.ngAfterViewInit();

        [this.loadExpressCheckoutParamsStatus, this.totalsUpdatingClickStatus].forEach(i => {
            this.subscriptions.add(i.subscribe(() => this.detectChanges()));
        });

        this.subscriptions.add(
            this.basketTotalsUpdatingService.subject.subscribe(() => {
                if (!this.totalsUpdating) {
                    this.totalsUpdatingClickStatus.reset();
                }
            })
        );
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();
        this.clearExpressCheckoutElementInstance();
    }

    /**
     * Checks if elements can be displayed
     * @returns {boolean}
     */
    protected canDisplayElements(): boolean {
        return !!this.container?.nativeElement;
    }

    /**
     * On stripe correctly created callback
     */
    protected onStripeCreated(): void {}

    /**
     * Returns if component is ready
     * @returns {boolean}
     */
    protected isComponentReady(): boolean {
        return !!this.expressCheckoutElementInstance;
    }

    protected override createElements(): void {
        this.clear();
        // we create elements later for this component
        this.onElementsCreated();

        this.readyCheck();
        this.detectChanges();
    }

    /**
     * On elements object created callback
     */
    protected onElementsCreated(): void {
        this.loadExpressCheckoutParams();
        this.checkoutStepComponent?.forms.forEach(form => {
            if (form && form.valueChanges) {
                this.subscriptions.add(form.valueChanges.subscribe(() => this.onCheckoutDataUpdateDebounced()));
            }
        });
        this.subscriptions.add(this.app.customer.basket.subscribe(() => this.onCheckoutDataUpdateDebounced()));
    }

    //////////////////////////////////////////
    /////
    /////   Checkout data update
    /////
    //////////////////////////////////////////

    /**
     * Loads express checkout params
     */
    private loadExpressCheckoutParams(): void {
        this.lastUpdateRequiredData = JSON.stringify(this.getUpdateRequiredData());
        this.stripeService.getExpressCheckoutParams(
            this.loadExpressCheckoutParamsStatus,
            { location: 'checkout' },
            params => {
                this.onExpressCheckoutParamsLoaded(params);
            },
            () => {
                this.onExpressCheckoutParamsLoaded();
            }
        );
    }

    /**
     * Clears/remove existing express checkout element instance
     */
    private clearExpressCheckoutElementInstance(): void {
        if (this.expressCheckoutElementInstance) {
            try {
                // this.expressCheckoutElementInstance.clear();
                this.expressCheckoutElementInstance.destroy();
                this.expressCheckoutElementInstance.unmount();
                this.expressCheckoutElementInstance = undefined;
            } catch (e) {
                this.expressCheckoutElementInstance = undefined;
            }
        }
        this.readyCheck();
    }

    /**
     * On express checkout element instance callback
     * @param {Stripe.IExpressCheckoutParams} params
     */
    private onExpressCheckoutParamsLoaded(params?: Stripe.IExpressCheckoutParams): void {
        if (!this.totalsUpdating) {
            this.totalsUpdatingClickStatus.reset();
        }

        this.clearExpressCheckoutElementInstance();

        if (params && this.stripe) {
            try {
                const elementsOptions = params.elementOptions;
                if (this.elements) {
                    this.elements.update(elementsOptions);
                } else {
                    this.elements = this.stripe.elements(elementsOptions);
                }
            } catch (e) {
                console.error('Error creating stripe elements', e);
            }

            if (this.container?.nativeElement) {
                if (this.elements) {
                    const expressCheckout = this.elements.create(Stripe.Component.ExpressCheckout, this.getExpressCheckoutCreateOptions());
                    expressCheckout.on(Stripe.ElementEvent.Ready, () => {
                        this.onExpressCheckoutReadyEvent(expressCheckout);
                    });

                    this.expressCheckoutElementInstance = expressCheckout;
                    expressCheckout.mount(this.container.nativeElement);
                }
            } else {
                console.error('Container not set');
            }
        }
        this.readyCheck();
        this.detectChanges();
    }

    private getExpressCheckoutCreateOptions(): Stripe.IExpressCheckoutCreateOptions {
        const options: Stripe.IExpressCheckoutCreateOptions = { ...(this.getConfig()?.buttonConfig || {}) };
        options.billingAddressRequired = false;
        options.emailRequired = false;
        options.phoneNumberRequired = false;
        options.shippingAddressRequired = false;
        if (!options.paymentMethods) {
            options.paymentMethods = {};
        }
        options.paymentMethods.applePay = this.applePayAvailability;
        options.paymentMethods.googlePay = this.googlePayAvailability;
        options.paymentMethods.link = this.linkAvailability;
        options.paymentMethods.amazonPay = 'never';
        options.paymentMethods.paypal = 'never';
        return options;
    }

    //////////////////////////////////////////
    /////
    /////   Express checkout element instance
    /////
    //////////////////////////////////////////

    /**
     * On express checkout element instance ready event handler
     * @param { Stripe.IExpressCheckoutElementInstance }     expressCheckout
     */
    private onExpressCheckoutReadyEvent(expressCheckout: Stripe.IExpressCheckoutElementInstance): void {
        expressCheckout.on(Stripe.EExpressCheckoutEvent.click, (event: Stripe.IExpressCheckoutClickEvent) => {
            this.onExpressCheckoutClickEvent(event);
        });

        expressCheckout.on(Stripe.EExpressCheckoutEvent.confirm, (event: Stripe.IExpressCheckoutConfirmEvent) => {
            this.onExpressCheckoutConfirmEvent(event);
        });

        // expressCheckout.on(
        //     Stripe.EExpressCheckoutEvent.shippingaddresschange,
        //     (event: Stripe.IExpressCheckoutShippingAddressChangeEvent) => {
        //         const shippingAddress = this.getShippingAddressFrom(event.address);
        //         this.shippingAddress = shippingAddress;
        //         const payload: Stripe.IExpressCheckoutShippingAddressChangedData = {
        //             newAddress: shippingAddress,
        //             location: 'checkout'
        //         };
        //         this.stripeService.expressCheckoutShippingAddressChanged(
        //             undefined,
        //             payload,
        //             (response: Stripe.IExpressCheckoutShippingResponse) => {
        //                 if (response?.resolvePayload?.shippingRates?.length) {
        //                     this.stripeService.expressCheckoutShippingRateChanges(
        //                         undefined,
        //                         {
        //                             address: shippingAddress,
        //                             shipping_id: response.resolvePayload.shippingRates[0].id
        //                         },
        //                         (ratesResponse: Stripe.IExpressCheckoutShippingResponse) => {
        //                             const shippingRates = ratesResponse.resolvePayload.shippingRates;
        //                             if (shippingRates?.length) {
        //                                 event.resolve({ shippingRates });
        //                             } else {
        //                                 event.reject();
        //                             }
        //                         },
        //                         () => {
        //                             event.reject();
        //                         }
        //                     );
        //                 } else {
        //                     event.reject();
        //                 }
        //             },
        //             () => {
        //                 event.reject();
        //             }
        //         );
        //     }
        // );

        // expressCheckout.on(Stripe.EExpressCheckoutEvent.shippingratechange, (event: Stripe.IExpressCheckoutShippingRateChangeEvent) => {
        //     const shippingId = event.shippingRate?.id;
        //     if (shippingId && this.shippingAddress) {
        //         const payload: Stripe.IExpressCheckoutShippingRateChangesData = {
        //             address: this.shippingAddress,
        //             shipping_id: shippingId
        //         };
        //         this.stripeService.expressCheckoutShippingRateChanges(
        //             undefined,
        //             payload,
        //             response => {
        //                 if (response?.resolvePayload?.shippingRates?.length) {
        //                     event.resolve({ shippingRates: response.resolvePayload.shippingRates });
        //                 } else {
        //                     event.reject();
        //                 }
        //             },
        //             () => {
        //                 event.reject();
        //             }
        //         );
        //     } else {
        //         event.reject();
        //     }
        // });
    }

    private onExpressCheckoutClickEvent(event: Stripe.IExpressCheckoutClickEvent): void {
        this.totalsUpdatingClickStatus.reset();
        const checkoutIsValid = this.checkoutFormComponent.isCheckoutValid;
        if (!checkoutIsValid || this.totalsUpdating || this.loadExpressCheckoutParamsStatus.busy || this.checkoutBusy) {
            // if checkout is busy we do nothing
            if (!this.checkoutBusy) {
                if (!checkoutIsValid) {
                    this.checkoutFormComponent.validateCheckout();
                } else {
                    this.totalsUpdatingClickStatus.error = 'Your order details are being updated. Please try again in a moment';
                }
            }
        } else {
            event.resolve();
        }
    }

    //////////////////////////////////////////
    /////
    /////   confirm event
    /////
    //////////////////////////////////////////

    /**
     * On express checkout confirm event handler
     * @param {Stripe.IExpressCheckoutConfirmEvent} event
     */
    private onExpressCheckoutConfirmEvent(event: Stripe.IExpressCheckoutConfirmEvent): void {
        const elements = this.elements;
        if (elements && this.stripe) {
            elements
                .submit()
                .then(() => {
                    if (this.stripe) {
                        const paymentMethodData = this.getCreateConfirmationTokenData(elements);

                        this.stripe
                            .createConfirmationToken(paymentMethodData)
                            .then((tokenResult: Stripe.ICreateConfirmationTokenResult) => {
                                const confirmationToken = tokenResult.confirmationToken;
                                if (confirmationToken) {
                                    const placeOrderData = this.getPlaceOrderData(event, confirmationToken);
                                    this.placeOrder(placeOrderData);
                                } else {
                                    event.paymentFailed({ reason: 'fail', message: tokenResult.error?.message || 'Payment failed' });
                                }
                            })
                            .catch(e => {
                                event.paymentFailed({ reason: 'fail', message: e.message });
                            });
                    }
                })
                .catch((error: Stripe.IError) => {
                    event.paymentFailed({ reason: 'fail', message: error.message });
                });
        } else {
            event.paymentFailed({ reason: 'fail', message: 'Payment failed' });
        }
    }

    private getPlaceOrderData(
        confirmEvent: Stripe.IExpressCheckoutConfirmEvent,
        confirmationToken: Stripe.IConfirmationToken
    ): Stripe.IExpressCheckoutPlaceOrderData {
        const data: Stripe.IExpressCheckoutPlaceOrderData = {
            location: 'checkout',
            result: { ...confirmEvent, confirmationToken }
        };

        const billingAddress = this.checkoutFormComponent.billingAddress;
        if (billingAddress) {
            data.result.billingDetails = {
                name: this.getNameFromAddress(billingAddress),
                email: this.checkoutFormComponent.email,
                phone: billingAddress.telephone,
                address: this.convertCheckoutAddressToStripeAddress(billingAddress)
            };
        }

        if (!this.checkoutFormComponent.isVirtual) {
            const shippingAddress = this.checkoutFormComponent.shippingAddress;
            if (shippingAddress) {
                data.result.shippingAddress = {
                    address: this.convertCheckoutAddressToStripeAddress(shippingAddress),
                    name: this.getNameFromAddress(shippingAddress)
                };
            }
            const shippingMethod = this.checkoutFormComponent.shippingMethod;
            if (shippingMethod) {
                data.result.shippingRate = {
                    id: shippingMethod.code,
                    displayName: shippingMethod.title,
                    amount: shippingMethod.price * 100
                };
            }
        }

        return data;
    }

    private placeOrder(data: Stripe.IExpressCheckoutPlaceOrderData): void {
        const status = this.checkoutFormComponent.status;

        status.superBusy = true;

        this.stripeService.expressCheckoutPlaceOrder(
            undefined,
            data,
            () => {
                this.checkoutFormComponent.redirectToSuccess();
            },
            (error?: { error?: string }) => {
                const errorMessage = error?.error;
                if (errorMessage && this.stripeService.isAuthenticationRequired(errorMessage) && this.stripe) {
                    const paymentIntents = this.stripeService.getPaymentIntentsFromErrorMessage(errorMessage!);
                    if (paymentIntents?.length) {
                        this.processAuthentication(paymentIntents, this.stripe)
                            .then(() => {
                                this.placeOrder(data);
                            })
                            .catch(e => {
                                const errorMessage =
                                    typeof e === 'string' ? e : typeof e?.error === 'string' ? e.error : Stripe.AUTH_ERROR_MESSAGE;
                                status.superBusy = false;
                                status.error = errorMessage;
                            });
                    } else {
                        status.superBusy = false;
                        status.error = Stripe.AUTH_ERROR_MESSAGE;
                    }
                } else {
                    status.superBusy = false;
                    status.error = 'Please try again with another form of payment.';
                }
            }
        );
    }

    private processAuthentication(paymentIntents: string[], stripe: Stripe.Instance): Promise<void> {
        return new Promise((resolve, reject) => {
            const process = () => {
                if (paymentIntents?.length > 0) {
                    const paymentIntent = paymentIntents.pop();
                    this.authenticateCustomer(paymentIntent || '', stripe)
                        .then(() => {
                            process();
                        })
                        .catch(reject);
                } else {
                    resolve();
                }
            };
            process();
        });
    }

    private authenticateCustomer(intentId: string, stripe: Stripe.Instance): Promise<void> {
        return new Promise((resolve, reject) => {
            try {
                const isPaymentIntent = intentId.startsWith('pi_');
                const isSetupIntent = intentId.startsWith('seti_');

                const handleIntent = (result: Stripe.IRetrieveIntentResponse) => {
                    if (result.error) {
                        reject(result.error.message);
                    } else {
                        const intent = result.paymentIntent || result.setupIntent;
                        const requiresActionStatuses = ['requires_action', 'requires_source_action'];

                        if (intent?.status && requiresActionStatuses.includes(intent.status)) {
                            stripe
                                .handleNextAction({ clientSecret: intent.client_secret })
                                .then(result => {
                                    if (result.error?.message) {
                                        reject(result.error.message);
                                    } else {
                                        resolve();
                                    }
                                })
                                .catch((e: Stripe.IError) => {
                                    reject(e?.message);
                                });
                        } else {
                            resolve();
                        }
                    }
                };

                if (isPaymentIntent) {
                    stripe.retrievePaymentIntent(intentId).then(handleIntent).catch(reject);
                } else if (isSetupIntent) {
                    stripe.retrieveSetupIntent(intentId).then(handleIntent).catch(reject);
                } else {
                    reject('Invalid intent id');
                }
            } catch (e: any) {
                reject(e?.message);
            }
        });
    }

    private getStripeShippingAddress(): Stripe.IAddress | undefined {
        if (!this.checkoutFormComponent.isVirtual) {
            const shippingAddress = this.checkoutFormComponent.shippingAddress;
            if (shippingAddress) {
                return this.convertCheckoutAddressToStripeAddress(shippingAddress);
            }
        }
        return undefined;
    }

    private convertCheckoutAddressToStripeAddress(address: Checkout.IAddress): Stripe.IAddress {
        return {
            city: address.city,
            country: address.country_id,
            postal_code: address.postcode,
            state: address.region || address.region_id?.toString(),
            line1: address.street_1,
            line2: address.street_2
        };
    }

    private getCreateConfirmationTokenData(elements: Stripe.IElementsInstance): Stripe.ICreateConfirmationTokenOptions {
        const data: Stripe.ICreateConfirmationTokenOptions = {
            elements,
            params: {}
        };

        if (data.params) {
            const stripeShippingAddress = this.getStripeShippingAddress();
            if (stripeShippingAddress) {
                data.params.shipping = {
                    address: stripeShippingAddress
                };
                const shippingAddress = this.checkoutFormComponent.shippingAddress;
                if (shippingAddress) {
                    data.params.shipping.phone = shippingAddress.telephone;
                    data.params.shipping.name = this.getNameFromAddress(shippingAddress);
                }
            }
            const billingAddress = this.checkoutFormComponent.billingAddress;
            if (billingAddress) {
                data.params.payment_method_data = {
                    billing_details: {
                        name: this.getNameFromAddress(billingAddress),
                        phone: billingAddress.telephone,
                        address: this.convertCheckoutAddressToStripeAddress(billingAddress),
                        email: this.checkoutFormComponent.email
                    }
                };
            }
        }
        return data;
    }

    private getNameFromAddress(address: Checkout.IAddress): string {
        return [address.firstname, address.lastname].filter(i => !!i).join(' ');
    }

    //////////////////////////////////////////
    /////
    /////   Checkout
    /////
    //////////////////////////////////////////

    /**
     * Returns if totals are updating
     * @returns {boolean}
     */
    private get totalsUpdating(): boolean {
        return this.basketTotalsUpdatingService.totalsUpdating;
    }

    //////////////////////////////////////////
    /////
    /////   Checkout data update
    /////
    //////////////////////////////////////////

    /**
     * Checkout submission is in progress
     * @returns {boolean}
     */
    get checkoutBusy(): boolean {
        return this.checkoutFormComponent.status.busy;
    }

    /**
     * On checkout data update
     */
    private onCheckoutDataUpdate(): void {
        if (this.isUpdateRequired()) {
            this.loadExpressCheckoutParams();
        }
    }

    /**
     * Checks if we have to create new express checkout element instance because of checkout data change
     * @returns {boolean}
     */
    private isUpdateRequired(): boolean {
        if (this.checkoutFormComponent.isFree) {
            return false;
        }
        return JSON.stringify(this.getUpdateRequiredData()) !== this.lastUpdateRequiredData;
    }

    /**
     * Returns update required data
     * @returns {Stripe.IPaymentRequestUpdateRequiredData}
     */
    private getUpdateRequiredData(): Stripe.IUpdateRequiredData {
        return {
            billingCountry: this.checkoutFormComponent.billingAddress?.country_id,
            total: this.app.customer.basket.data?.totals?.grand_total,
            items: this.app.customer.basket.items.map(basketItem => {
                return {
                    amount: basketItem.rowPrice(),
                    label: basketItem.product?.data?.name,
                    id: basketItem.data?.id
                };
            })
        };
    }

    //////////////////////////////////////////
    /////
    /////   Stripe methods
    /////
    //////////////////////////////////////////

    /**
     * Returns stripe options
     * @returns {Stripe.InstanceOptions}
     */
    protected override getStripeOptions(): Stripe.InstanceOptions {
        return this.method?.config?.initParams?.options || {};
    }

    //////////////////////////////////////////
    /////
    /////   Helpers
    /////
    //////////////////////////////////////////

    private getShippingAddressFrom(address: Stripe.IAddress): Stripe.IAddress {
        address = { ...address };
        if (address?.region && (!address.city || (address?.city?.length === 0 && address.region?.length > 0))) {
            address.city = address.region;
        }
        return address;
    }
}
